/**
 * The month lists behind every month picker.
 *
 * Planners pick a month from a list rather than the browser's month/year spinner, which
 * made "three months from now" a fiddly two-part click. A list puts the next few months
 * one click away. Months are `YYYY-MM` strings and all arithmetic is integer month
 * ordinals — never a parsed Date, which would drift a month west of Greenwich.
 */

import { monthRange, shiftMonth } from "./plan-window";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** How far back a start month reaches — a plan is sometimes written after work began. */
export const START_MONTHS_BACK = 6;
/** How far ahead a start month reaches. */
export const START_MONTHS_AHEAD = 24;
/** The longest plan the end list offers: three years, start month included. */
export const MAX_PLAN_MONTHS = 36;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export interface MonthOption {
  value: string;
  label: string;
  /** A muted hint beside the label: "this month", "6 months". */
  hint?: string;
}

/** The current month, from LOCAL date parts (`toISOString` is UTC and can name last month). */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** `"2026-09"` → `"September 2026"`. */
export function monthLabel(month: string): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1];
  return name === undefined ? month : `${name} ${month.slice(0, 4)}`;
}

/**
 * Keep a value the list would otherwise drop — an older plan's start month, say — so the
 * picker still shows what is saved instead of going blank.
 */
function withSelected(options: MonthOption[], selected: string | null | undefined): MonthOption[] {
  if (selected == null || !MONTH.test(selected)) return options;
  if (options.some((o) => o.value === selected)) return options;
  return [...options, { value: selected, label: monthLabel(selected) }].sort((a, b) =>
    a.value.localeCompare(b.value),
  );
}

/** Months around today, with today marked. For start months and "from" dates. */
export function monthsAroundToday(options: {
  back: number;
  ahead: number;
  selected?: string | null;
  today?: string;
}): MonthOption[] {
  const today = options.today ?? currentMonth();
  const list = monthRange(shiftMonth(today, -options.back), options.back + options.ahead + 1).map(
    (value): MonthOption => ({
      value,
      label: monthLabel(value),
      ...(value === today ? { hint: "this month" } : {}),
    }),
  );
  return withSelected(list, options.selected);
}

/** A plan's start-month list: six months back, two years ahead. */
export function startMonthOptions(selected?: string | null, today?: string): MonthOption[] {
  return monthsAroundToday({
    back: START_MONTHS_BACK,
    ahead: START_MONTHS_AHEAD,
    selected,
    ...(today === undefined ? {} : { today }),
  });
}

/**
 * A plan's end-month list: the start month itself (a one-month plan) through a three-year
 * plan, each with its length so "a six-month plan" is a glance, not a sum.
 */
export function endMonthOptions(start: string, selected?: string | null): MonthOption[] {
  if (!MONTH.test(start)) return [];
  const list = monthRange(start, MAX_PLAN_MONTHS).map(
    (value, i): MonthOption => ({
      value,
      label: monthLabel(value),
      hint: i === 0 ? "1 month" : `${i + 1} months`,
    }),
  );
  // An end before the start is invalid — don't carry one forward into the list.
  return selected != null && selected >= start ? withSelected(list, selected) : list;
}
